<script>
  import DynamicHeading from "../atoms/DynamicHeading.svelte";
  import DynamicHeadingContext from "../atoms/DynamicHeadingContext.svelte";
  import Image from "../atoms/Image.svelte";
  import Link from "../atoms/Link.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /** @type { string } */
  let className = "";
  export { className as class };

  /**
   * List of content items to be displayed in the word cloud. Each item is an object with a `type` property
   * that can be either "text" or "image", and a `content` property that is either the text to be displayed
   * or the URL of the image to be displayed.
   * @type {Array<{type: string, content: string}>}
   */
  export let contentList;

  /**
   * The URL to which the WordCloud component will link.
   * If not provided, the WordCloud will not include a link.
   * @type {string}
   */
  export let linkUrl = undefined;

  /**
   * The label for the link in the WordCloud component.
   * If not provided, the WordCloud will not include a link.
   * @type {string}
   */
  export let linkLabel = undefined;

  /**
   * whether the link is external
   *
   * @type {boolean}
   */
  export let isLinkExternal = false;

  /** @type {HTMLElement | undefined} */
  export let ref = undefined;

  /**
   * If the WordCloud component is intersecting with the viewport
   * @type {boolean}
   */
  export let isIntersecting = false;

  /**
   * Animate the WordCloud component when it comes into view
   * @type {boolean}
   */
  export let animateOnView = false;

  let animationClass = "ease-spring-easing transition-all duration-[300ms]";
  $: intersectingAnimation = isIntersecting
    ? "translate-y-0 opacity-[1]"
    : "translate-y-full opacity-[0]";
  $: animationItemClass = animateOnView ? intersectingAnimation : "";

  let parsedContentList = [];
  let contentListText = [];

  contentList?.forEach((item) => {
    if (item.type === "text") {
      // Skip if it's empty content
      if (item.content.length === 0) {
        return;
      }

      const wordList = item.content.split(" ");
      wordList.forEach((word) => {
        parsedContentList.push({
          content: word,
          type: item.type
        });
        contentListText.push(word);
      });
    } else {
      parsedContentList.splice(parsedContentList.length, 0, {
        content: item.content,
        srcset: item.srcset,
        sizes: item.sizes,
        type: item.type
      });
    }
  });

  const ariaLabelText = contentListText.join(" ");
</script>

{#if contentList?.length}
  <DynamicHeadingContext>
    <div {id} bind:this={ref}>
      <DynamicHeading
        class="heading-md flex flex-wrap items-baseline text-pretty {className}"
        aria-label={ariaLabelText}
      >
        {#each parsedContentList as contentItem, index (index)}
          {#if contentItem?.type === "image"}
            <span
              class="image mr-2 inline-block h-[1em] overflow-hidden pt-2 md:mr-3 lg:mr-4 xl:pt-5"
            >
              <span
                class="block {animationClass} {animationItemClass} h-full"
                style="transition-delay: {index * 50}ms;"
              >
                <Image
                  aspectRatioClasses="aspect-3/2"
                  containerClass="h-full "
                  src={contentItem.content}
                  srcset={contentItem.srcset}
                  sizes={contentItem.sizes}
                  alt=""
                />
              </span>
            </span>
          {:else if contentItem?.type === "text"}
            <span class="word inline-block whitespace-pre">
              <span
                class="inline-block {animationClass} {animationItemClass}"
                style="transition-delay: {index * 50}ms;"
              >
                {`${contentItem.content} `}
              </span>
            </span>
          {/if}
        {/each}
        {#if linkUrl && linkLabel}
          <span class="inline-block self-end pt-[0.4rem]">
            <span
              class="inline-block {animationClass} {animationItemClass}"
              style="transition-delay: {parsedContentList.length * 50}ms;"
            >
              <Link label={linkLabel} href={linkUrl} variant="large" isExternal={isLinkExternal} />
            </span>
          </span>
        {/if}
      </DynamicHeading>
    </div>
  </DynamicHeadingContext>
{/if}
